import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";
import { fetch3 } from '../commonApis/commonApis';
import { logout } from '../features/auth/authSlice';

export const useIdleTimeout = (isAuthenticated: boolean, idleTime = 30 * 60 * 1000) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const url = process.env.REACT_APP_BASE_URL;
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const performLogout = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      await fetch3(`${url}/api/auth/logout?type=lmClient`, "get");
    } catch (error) {
      console.error("Logout API failed", error);
    }

    // clear token
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");

    // clear profile query cache so app knows user is logged out
    queryClient.removeQueries({ queryKey: ["getMe"] });

    dispatch(logout());
    navigate("/", { replace: true });
  }, [isAuthenticated, url, dispatch, navigate, queryClient]);

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    // Only set timeout if user is logged in
    if (isAuthenticated) {
      timeoutRef.current = setTimeout(() => {
        performLogout();
      }, idleTime);
    }
  }, [performLogout, idleTime, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Events that represent user activity
    const events = [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mouseWheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'scroll'
    ];
    
    const handleEvent = () => resetTimer();
    
    // Add event listeners
    events.forEach((event) => 
      document.addEventListener(event, handleEvent, { passive: true })
    );
    
    // Start the initial timer
    resetTimer();

    return () => {
      // Cleanup event listeners
      events.forEach((event) => 
        document.removeEventListener(event, handleEvent)
      );
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [resetTimer, isAuthenticated]);
};
